/**
 * Client-side guard for side panel card HTML (vocabulary sp@2).
 *
 * Card HTML is painted without Angular's sanitizer (it would strip the data-* attributes the design depends on),
 * so everything outside the vocabulary is removed here before it reaches the page: unknown elements with their
 * content, unknown attributes, non-sp classes and any link or image URL that isn't https.
 * The admin validates cards on save; this is the second line of defence on the course page and the preview.
 */

const ALLOWED_TAGS = new Set<string>([
  'aside', 'p', 'h2', 'span', 'ul', 'li', 'dl', 'dt', 'dd', 'figure', 'figcaption', 'img', 'div',
  'b', 'del', 'strong', 'em', 'a', 'br', 'blockquote', 'cite', 'hr', 'small'
]);

const ALLOWED_ATTRIBUTES = new Set<string>([
  'class', 'href', 'src', 'alt', 'data-tone', 'data-icon', 'data-role', 'data-score', 'data-ratio', 'data-emphasis'
]);

const URL_ATTRIBUTES = new Set<string>(['href', 'src']);

/**
 * Parses card HTML into inert DOM and returns the cleaned <aside class="sp"> cards it contains.
 * Anything outside an aside.sp (for example old-format notification HTML) is dropped.
 */
export function guardCardHtml(html: string): HTMLElement[] {
  const template = document.createElement('template');
  template.innerHTML = (html || '').trim();
  const cards: HTMLElement[] = [];
  Array.prototype.forEach.call(template.content.children, (child: Element) => {
    if (child.tagName.toLowerCase() === 'aside' && child.classList.contains('sp')) {
      cleanElement(child);
      cards.push(child as HTMLElement);
    }
  });
  return cards;
}

function cleanElement(element: Element): void {
  cleanAttributes(element);
  Array.prototype.slice.call(element.childNodes).forEach((node: Node) => {
    if (node.nodeType === Node.ELEMENT_NODE) {
      const child = node as Element;
      if (ALLOWED_TAGS.has(child.tagName.toLowerCase())) {
        cleanElement(child);
      } else {
        element.removeChild(child);
      }
    } else if (node.nodeType !== Node.TEXT_NODE) {
      element.removeChild(node);
    }
  });
}

function cleanAttributes(element: Element): void {
  Array.prototype.slice.call(element.attributes).forEach((attribute: Attr) => {
    const name = attribute.name.toLowerCase();
    if (!ALLOWED_ATTRIBUTES.has(name)) {
      element.removeAttribute(attribute.name);
    } else if (URL_ATTRIBUTES.has(name) && !isHttpsUrl(attribute.value)) {
      element.removeAttribute(attribute.name);
    } else if (name === 'class') {
      const classes = attribute.value.split(/\s+/).filter(c => /^sp(-[a-z]+)?$/.test(c));
      if (classes.length) {
        element.setAttribute('class', classes.join(' '));
      } else {
        element.removeAttribute('class');
      }
    }
  });
}

function isHttpsUrl(value: string): boolean {
  return /^https:\/\/[^\s/]+/i.test((value || '').trim());
}
